/**
 * StorageService - Handles local data storage and caching
 * Provides offline functionality and fast initial loading
 */

import { SummaryStats, DistrictStats, VaccineSite } from '@/api/ApiService';

export interface CachedData {
  stats: SummaryStats;
  districts: DistrictStats[];
  vaccines?: VaccineSite[];
  last_updated: string;
  cache_timestamp: string;
}

export interface UserPreferences {
  selectedDistrict?: string;
  language: 'en' | 'kri';
  notifications: boolean;
  darkMode?: boolean;
  lastVisit: string;
}

export class StorageService {
  private readonly CACHE_KEY = 'mpox_tracker_cache';
  private readonly PREFERENCES_KEY = 'mpox_tracker_prefs';
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

  constructor() {
    this.init();
  }

  private init(): void {
    // Check if localStorage is available
    if (!this.isStorageAvailable()) {
      console.warn('localStorage not available - offline functionality disabled');
    }

    // Clean up old cache entries on init
    this.cleanupExpiredCache();
  }

  private isStorageAvailable(): boolean {
    try {
      const test = '__storage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Save stats and district data to cache
   */
  async saveStats(data: {
    stats: SummaryStats;
    districts: DistrictStats[];
    vaccines?: VaccineSite[];
  }): Promise<void> {
    if (!this.isStorageAvailable()) return;

    try {
      const cacheData: CachedData = {
        ...data,
        last_updated: data.stats.last_updated,
        cache_timestamp: new Date().toISOString()
      };

      localStorage.setItem(this.CACHE_KEY, JSON.stringify(cacheData));
      console.log('Data cached successfully');
    } catch (error) {
      console.error('Failed to cache data:', error);
      // Try to free up space by clearing old cache
      this.clearCache();
    }
  }

  /**
   * Get cached stats data
   */
  async getStats(): Promise<CachedData | null> {
    if (!this.isStorageAvailable()) return null;

    try {
      const cached = localStorage.getItem(this.CACHE_KEY);
      if (!cached) return null;

      const data: CachedData = JSON.parse(cached);
      
      // Check if cache is still valid
      if (this.isCacheExpired(data.cache_timestamp)) {
        console.log('Cache expired, removing...');
        this.clearCache();
        return null;
      }

      console.log('Using cached data');
      return data;
    } catch (error) {
      console.error('Failed to retrieve cached data:', error);
      this.clearCache();
      return null;
    }
  }

  /**
   * Check if cached data is still fresh
   */
  isCacheValid(): boolean {
    if (!this.isStorageAvailable()) return false;

    try {
      const cached = localStorage.getItem(this.CACHE_KEY);
      if (!cached) return false;

      const data: CachedData = JSON.parse(cached);
      return !this.isCacheExpired(data.cache_timestamp);
    } catch {
      return false;
    }
  }

  private isCacheExpired(cacheTimestamp: string): boolean {
    const now = new Date().getTime();
    const cacheTime = new Date(cacheTimestamp).getTime();
    return (now - cacheTime) > this.CACHE_DURATION;
  }

  /**
   * Clear all cached data
   */
  clearCache(): void {
    if (!this.isStorageAvailable()) return;
    
    try {
      localStorage.removeItem(this.CACHE_KEY);
      console.log('Cache cleared');
    } catch (error) {
      console.error('Failed to clear cache:', error);
    }
  }

  /**
   * Clean up expired cache entries
   */
  private cleanupExpiredCache(): void {
    if (!this.isCacheValid()) {
      this.clearCache();
    }
  }

  /**
   * Save user preferences
   */
  async savePreferences(preferences: Partial<UserPreferences>): Promise<void> {
    if (!this.isStorageAvailable()) return;

    try {
      const existing = await this.getPreferences();
      const updated: UserPreferences = {
        ...existing,
        ...preferences,
        lastVisit: new Date().toISOString()
      };

      localStorage.setItem(this.PREFERENCES_KEY, JSON.stringify(updated));
      console.log('Preferences saved');
    } catch (error) {
      console.error('Failed to save preferences:', error);
    }
  }

  /**
   * Get user preferences
   */
  async getPreferences(): Promise<UserPreferences> {
    const defaults: UserPreferences = {
      language: 'en',
      notifications: true,
      lastVisit: new Date().toISOString()
    };

    if (!this.isStorageAvailable()) return defaults;

    try {
      const stored = localStorage.getItem(this.PREFERENCES_KEY);
      if (!stored) return defaults;

      const preferences: UserPreferences = JSON.parse(stored);
      return { ...defaults, ...preferences };
    } catch (error) {
      console.error('Failed to get preferences:', error);
      return defaults;
    }
  }

  /**
   * Save search history for autocomplete
   */
  async saveSearch(query: string): Promise<void> {
    if (!this.isStorageAvailable() || !query.trim()) return;

    try {
      const key = 'mpox_search_history';
      const existing = JSON.parse(localStorage.getItem(key) || '[]');
      
      // Add new search to beginning, remove duplicates, limit to 10
      const updated = [
        query.trim(),
        ...existing.filter((item: string) => item !== query.trim())
      ].slice(0, 10);

      localStorage.setItem(key, JSON.stringify(updated));
    } catch (error) {
      console.error('Failed to save search:', error);
    }
  }

  /**
   * Get search history
   */
  async getSearchHistory(): Promise<string[]> {
    if (!this.isStorageAvailable()) return [];

    try {
      const key = 'mpox_search_history';
      const history = localStorage.getItem(key);
      return history ? JSON.parse(history) : [];
    } catch (error) {
      console.error('Failed to get search history:', error);
      return [];
    }
  }

  /**
   * Get cache statistics for debugging
   */
  getCacheInfo(): {
    hasCache: boolean;
    cacheSize: number;
    cacheAge: number | null;
    isExpired: boolean;
  } {
    if (!this.isStorageAvailable()) {
      return {
        hasCache: false,
        cacheSize: 0,
        cacheAge: null,
        isExpired: true
      };
    }

    try {
      const cached = localStorage.getItem(this.CACHE_KEY);
      if (!cached) {
        return {
          hasCache: false,
          cacheSize: 0,
          cacheAge: null,
          isExpired: true
        };
      }

      const data: CachedData = JSON.parse(cached);
      const cacheAge = new Date().getTime() - new Date(data.cache_timestamp).getTime();
      
      return {
        hasCache: true,
        cacheSize: new Blob([cached]).size,
        cacheAge,
        isExpired: this.isCacheExpired(data.cache_timestamp)
      };
    } catch {
      return {
        hasCache: false,
        cacheSize: 0,
        cacheAge: null,
        isExpired: true
      };
    }
  }

  /**
   * Estimate storage usage
   */
  getStorageUsage(): {
    used: number;
    available: number;
    percentage: number;
  } {
    if (!this.isStorageAvailable()) {
      return { used: 0, available: 0, percentage: 0 };
    }

    try {
      let used = 0;
      for (const key in localStorage) {
        if (localStorage.hasOwnProperty(key)) {
          used += localStorage[key].length + key.length;
        }
      }

      // LocalStorage typically has ~5MB limit
      const available = 5 * 1024 * 1024; // 5MB in bytes
      const percentage = (used / available) * 100;

      return { used, available, percentage };
    } catch {
      return { used: 0, available: 0, percentage: 0 };
    }
  }

  /**
   * Clear all app data (for privacy/reset)
   */
  clearAllData(): void {
    if (!this.isStorageAvailable()) return;

    try {
      // Remove all mpox tracker related data
      const keysToRemove = Object.keys(localStorage).filter(key => 
        key.startsWith('mpox_')
      );

      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      console.log('All app data cleared');
    } catch (error) {
      console.error('Failed to clear all data:', error);
    }
  }
}